import { X, Award, Trophy, Crown, Sparkles, CheckCircle, Target, TrendingUp, Brain, Users, Zap, Star, ArrowRight } from 'lucide-react';

interface CertificationPRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CertificationPRIsMEModal({ isOpen, onClose }: CertificationPRIsMEModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl max-w-7xl w-full max-h-[90vh] border border-purple-500/30 shadow-2xl shadow-purple-500/20 flex flex-col">
        {/* Header */}
        <div className="relative p-8 border-b border-purple-500/30">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                <Award className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Certification PRIsME
                </h2>
                <p className="text-gray-400 mt-1 text-lg">
                  Le parcours d'excellence qui transforme des bons consultants en LÉGENDES
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 space-y-10 overflow-y-auto flex-1">
          
          {/* Vision Message */}
          <div className="bg-gradient-to-r from-orange-600/20 via-red-600/20 to-orange-600/20 rounded-xl p-8 border-2 border-orange-500/50">
            <h3 className="text-3xl font-bold text-center text-white mb-4">
              🎯 Pourquoi la Certification PRIsME?
            </h3>
            <p className="text-gray-300 text-center text-lg mb-4">
              Si on veut que le monde ADHÈRE, devienne MEILLEUR, et que le client GAGNE par notre excellence...
            </p>
            <p className="text-white text-center text-xl font-bold">
              On doit MESURER, RECONNAÎTRE et RÉCOMPENSER la maîtrise d'PRIsME.
            </p>
          </div>

          {/* Les 4 Niveaux de Certification */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-8">
              🏆 Les 4 Niveaux de Maîtrise PRIsME
            </h3>

            {/* Niveau 1: PRIsME Certifié */}
            <div className="bg-gradient-to-r from-amber-600/10 to-orange-600/10 rounded-xl p-6 border-2 border-amber-500/50">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/50">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-amber-400 mb-2 flex items-center gap-2">
                    🥉 Niveau 1: PRIsME Certifié
                  </h4>
                  <p className="text-gray-400 text-sm mb-3">Durée typique: 0-6 mois</p>
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">Tu comprends et appliques PRIsME.</strong> Tu es capable de suivre le workflow, utiliser les outils, et livrer des livrables de qualité avec supervision.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-amber-500/20 rounded-lg p-4 border border-amber-500/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Critères d'Évaluation
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• Quiz théorique PRIsME (80%+)</li>
                        <li>• 2 projets clients complétés</li>
                        <li>• Maîtrise des 6 étapes du workflow</li>
                        <li>• Mindset IA démontré</li>
                      </ul>
                    </div>
                    <div className="bg-amber-500/20 rounded-lg p-4 border border-amber-500/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Avantages Débloqués
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• Badge officiel PRIsME Certifié</li>
                        <li>• +1% salaire de base</li>
                        <li>• Accès clients PME</li>
                        <li>• Nom sur site web BZ</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Niveau 2: PRIsME Expert */}
            <div className="bg-gradient-to-r from-gray-400/10 to-gray-500/10 rounded-xl p-6 border-2 border-gray-400/50">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center shadow-lg shadow-gray-400/50">
                    <Trophy className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-300 mb-2 flex items-center gap-2">
                    🥈 Niveau 2: PRIsME Expert
                  </h4>
                  <p className="text-gray-400 text-sm mb-3">Durée typique: 6-18 mois</p>
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">Tu maîtrises et optimises PRIsME.</strong> Tu livres de l'excellence de manière autonome, tu identifies des opportunités d'amélioration, et tu commences à guider les autres.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-400/20 rounded-lg p-4 border border-gray-400/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Critères d'Évaluation
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• 10+ projets clients excellents</li>
                        <li>• Score NPS client 9+ (moy.)</li>
                        <li>• 3 innovations PRIsME apportées</li>
                        <li>• Mentorat 2+ juniors</li>
                      </ul>
                    </div>
                    <div className="bg-gray-400/20 rounded-lg p-4 border border-gray-400/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Avantages Débloqués
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• Badge PRIsME Expert</li>
                        <li>• +3% salaire de base</li>
                        <li>• Accès clients Entreprise</li>
                        <li>• Possibilité de mentorer</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Niveau 3: PRIsME Maître */}
            <div className="bg-gradient-to-r from-yellow-400/10 to-amber-500/10 rounded-xl p-6 border-2 border-yellow-400/50">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-yellow-400/50">
                    <Crown className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-yellow-400 mb-2 flex items-center gap-2">
                    🥇 Niveau 3: PRIsME Maître
                  </h4>
                  <p className="text-gray-400 text-sm mb-3">Durée typique: 18+ mois</p>
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">Tu innoves et enseignes PRIsME.</strong> Tu définis les standards d'excellence, tu formes l'équipe, et tes clients deviennent des ambassadeurs BZ.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-yellow-400/20 rounded-lg p-4 border border-yellow-400/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Critères d'Évaluation
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• 25+ projets transformationnels</li>
                        <li>• 3+ clients références publiques</li>
                        <li>• Contribution majeure PRIsME</li>
                        <li>• Formation de 5+ Experts</li>
                      </ul>
                    </div>
                    <div className="bg-yellow-400/20 rounded-lg p-4 border border-yellow-400/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Avantages Débloqués
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• Badge PRIsME Maître</li>
                        <li>• +5% salaire de base</li>
                        <li>• Clients stratégiques exclusifs</li>
                        <li>• Part aux profits PRIsME</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Niveau 4: PRIsME Légende */}
            <div className="bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 rounded-xl p-6 border-2 border-purple-400/50 shadow-xl shadow-purple-500/30">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/50 animate-pulse">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-2 flex items-center gap-2">
                    💎 Niveau 4: PRIsME Légende
                  </h4>
                  <p className="text-gray-400 text-sm mb-3">L'Élite Absolue</p>
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">Tu transformes l'industrie TI.</strong> Ton nom est synonyme d'excellence. Tes méthodes deviennent standards. Tu bâtis l'avenir.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-purple-500/20 rounded-lg p-4 border border-purple-400/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Critères d'Évaluation
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• Impact industrie mesurable</li>
                        <li>• Publications / Conférences</li>
                        <li>• Création de nouvelles méthodologies</li>
                        <li>• Reconnaissance externe BZ</li>
                      </ul>
                    </div>
                    <div className="bg-purple-500/20 rounded-lg p-4 border border-purple-400/50">
                      <p className="text-white font-bold mb-2 flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Avantages Débloqués
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li>• Badge PRIsME Légende</li>
                        <li>• Package leadership exécutif</li>
                        <li>• Equity dans BZ</li>
                        <li>• Immortalité professionnelle 😎</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Les 6 Piliers d'Évaluation */}
          <div className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 rounded-xl p-8 border border-indigo-500/30">
            <h3 className="text-2xl font-bold text-center text-white mb-6">
              📊 Les 6 Piliers d'Évaluation PRIsME
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-indigo-500/20 rounded-lg p-4 border border-indigo-500/50">
                <Brain className="w-8 h-8 text-indigo-400 mb-2" />
                <p className="text-white font-bold">1. Mindset IA</p>
                <p className="text-gray-300 text-sm">Excellence, curiosité, humilité</p>
              </div>
              <div className="bg-purple-500/20 rounded-lg p-4 border border-purple-500/50">
                <Zap className="w-8 h-8 text-purple-400 mb-2" />
                <p className="text-white font-bold">2. Workflow PRIsME</p>
                <p className="text-gray-300 text-sm">Maîtrise des 6 étapes</p>
              </div>
              <div className="bg-pink-500/20 rounded-lg p-4 border border-pink-500/50">
                <Target className="w-8 h-8 text-pink-400 mb-2" />
                <p className="text-white font-bold">3. Compétences Tech</p>
                <p className="text-gray-300 text-sm">Outils, automatisation, IA</p>
              </div>
              <div className="bg-cyan-500/20 rounded-lg p-4 border border-cyan-500/50">
                <Users className="w-8 h-8 text-cyan-400 mb-2" />
                <p className="text-white font-bold">4. Relations Client</p>
                <p className="text-gray-300 text-sm">Onboarding, communication</p>
              </div>
              <div className="bg-green-500/20 rounded-lg p-4 border border-green-500/50">
                <TrendingUp className="w-8 h-8 text-green-400 mb-2" />
                <p className="text-white font-bold">5. Leadership</p>
                <p className="text-gray-300 text-sm">Mentorat, transmission</p>
              </div>
              <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-500/50">
                <CheckCircle className="w-8 h-8 text-orange-400 mb-2" />
                <p className="text-white font-bold">6. Livrables</p>
                <p className="text-gray-300 text-sm">Qualité, impact mesurable</p>
              </div>
            </div>
          </div>

          {/* Impact sur la Carrière */}
          <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl p-8 border-2 border-green-500/50">
            <h3 className="text-2xl font-bold text-center text-white mb-6">
              🚀 Impact sur Ta Carrière chez BZ
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <ArrowRight className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold">Parcours de carrière CLAIR</p>
                  <p className="text-gray-300">Tu sais exactement où tu es, et ce qu'il faut pour progresser.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <ArrowRight className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold">Reconnaissance MÉRITÉE</p>
                  <p className="text-gray-300">Ton excellence est mesurée, reconnue, et récompensée financièrement.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <ArrowRight className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold">Amélioration CONTINUE</p>
                  <p className="text-gray-300">La certification te pousse à toujours devenir meilleur, jamais stagner.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <ArrowRight className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold">Valeur sur le MARCHÉ</p>
                  <p className="text-gray-300">Une certification PRIsME devient un atout précieux dans l'industrie.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Message Final */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 rounded-xl p-8 border-2 border-purple-400">
            <h3 className="text-3xl font-bold text-center text-white mb-4">
              💎 PRIsME Certifié = Meilleur Skill de Ta Carrière
            </h3>
            <p className="text-white text-center text-lg mb-4">
              Maîtriser PRIsME, c'est maîtriser l'<strong>IA</strong>, l'<strong>excellence</strong>, la <strong>stratégie</strong>, et l'<strong>exécution</strong>.
            </p>
            <p className="text-purple-100 text-center text-xl font-bold">
              Tu ne deviens pas juste bon dans PRIsME. Tu deviens EXCELLENT dans TOUT.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-purple-500/30 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              Certification PRIsME • Excellence Mesurable • Carrière Accélérée
            </p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-105"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

